import React, { useState } from "react";
import { Form, FloatingLabel, Modal } from "react-bootstrap";
import i18n from "../../../../utils/i18n";

const ProfessionalIndemnity = ({
  user,
  setUser,
  onSubmit,
  progress,
}) => {
  const [showCoverageModal, setShowCoverageModal] = useState(false);
  const [validationError, setValidationError] = useState("");

  // Sri Lankan mobile number validation
  const validateSriLankanMobile = (phone) => {
    if (!phone) return true; // Allow empty if email is provided
    // Sri Lankan mobile numbers: start with 07 and have 10 digits total
    const sriLankanMobileRegex = /^0(7[0-9])\d{7}$/;
    return sriLankanMobileRegex.test(phone.replace(/\s/g, ""));
  };

  // Sri Lankan NIC validation
  const validateSriLankanNIC = (nic) => {
    if (!nic) return false;
    const nicValue = nic.trim().toUpperCase();

    // Old NIC format: 9 digits + V/X (e.g., 123456789V)
    const oldNICRegex = /^[0-9]{9}[VvXx]$/;
    // New NIC format: 12 digits (e.g., 199812345678)
    const newNICRegex = /^[0-9]{12}$/;

    return oldNICRegex.test(nicValue) || newNICRegex.test(nicValue);
  };

  const handleGetQuote = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (progress) return;

    // Validate: Either mobile OR email is required
    if (!user.phone && !user.email) {
      setValidationError("Please provide either a mobile number or email address");
      return;
    }

    // Validate NIC
    if (!user.nic || !validateSriLankanNIC(user.nic)) {
      setValidationError("Please enter a valid Sri Lankan NIC (e.g., 123456789V or 199812345678)");
      return;
    }

    // Validate mobile number if provided
    if (user.phone && !validateSriLankanMobile(user.phone)) {
      setValidationError("Please enter a valid Sri Lankan mobile number (e.g., 0771234567)");
      return;
    }

    // Clear any previous validation errors
    setValidationError("");

    // Submit the form first
    if (onSubmit) {
      const fakeEvent = { preventDefault: () => {} };
      if (onSubmit(fakeEvent) === false) return;
    }

    // Then show coverage details modal
    setShowCoverageModal(true);
  };

  return (
    <>
      <div role={"presentation"}>
        <FloatingLabel controlId="piName" label="Name*">
          <Form.Control
            type="text"
            placeholder="Name*"
            value={user.name}
            onChange={(e) => {
              setUser({
                ...user,
                name: e.target.value,
              });
            }}
            required
          />
        </FloatingLabel>

        <FloatingLabel controlId="piNIC" label="NIC*">
          <Form.Control
            type="text"
            placeholder="NIC*"
            value={user.nic}
            onChange={(e) => {
              setUser({
                ...user,
                nic: e.target.value,
              });
              setValidationError("");
            }}
            onBlur={(e) => {
              if (e.target.value && !validateSriLankanNIC(e.target.value)) {
                setValidationError("Please enter a valid Sri Lankan NIC (e.g., 123456789V or 199812345678)");
              }
            }}
            required
          />
        </FloatingLabel>

        <FloatingLabel
          controlId="piMobile"
          label={`${i18n.t("Your Mobile Number")}${!user.email ? '*' : ''}`}
        >
          <Form.Control
            type="tel"
            placeholder={`${i18n.t("Your Mobile Number")}${!user.email ? '*' : ''}`}
            value={user.phone}
            onChange={(e) => {
              setUser({ ...user, phone: e.target.value });
              setValidationError("");
            }}
            onBlur={(e) => {
              if (e.target.value && !validateSriLankanMobile(e.target.value)) {
                setValidationError("Please enter a valid Sri Lankan mobile number (e.g., 0771234567)");
              }
            }}
            required={!user.email}
          />
        </FloatingLabel>

        <FloatingLabel controlId="piEmail" label={`${i18n.t("Your Email")}${!user.phone ? '*' : ''}`}>
          <Form.Control
            type="email"
            placeholder={`${i18n.t("Your Email")}${!user.phone ? '*' : ''}`}
            value={user.email}
            onChange={(e) => {
              setUser({
                ...user,
                email: e.target.value,
              });
              setValidationError("");
            }}
            required={!user.phone}
          />
        </FloatingLabel>

        {validationError && (
          <div className="form-fail" style={{ marginBottom: "1rem" }}>
            {validationError}
          </div>
        )}

        <button
          className="primary-btn-sm"
          type="button"
          onClick={handleGetQuote}
          disabled={progress}
        >
          Get a Quote
        </button>
      </div>

      {/* Coverage Details Modal */}
      <Modal
        show={showCoverageModal}
        onHide={() => setShowCoverageModal(false)}
        centered
        size="md"
        style={{
          fontFamily: "Avenir, Arial, sans-serif"
        }}
      >
        <Modal.Header closeButton style={{ border: "none", paddingBottom: 0 }}>
        </Modal.Header>
        <Modal.Body style={{ padding: "2rem 2.5rem" }}>
          {/* Success Icon */}
          <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: "#00C853",
              margin: "0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white"/>
              </svg>
            </div>
          </div>

          {/* Title Section */}
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <p style={{
              fontSize: "0.75rem",
              color: "#666",
              textTransform: "uppercase",
              letterSpacing: "1px",
              marginBottom: "0.5rem"
            }}>
              LIMITS OF LIABILITY
            </p>
            <h4 style={{
              fontSize: "1.5rem",
              color: "#1c2a59",
              fontWeight: "bold",
              marginBottom: 0
            }}>
              Per Doctor
            </h4>
          </div>

          {/* Coverage Details Box */}
          <div style={{
            backgroundColor: "#f8f9fa",
            padding: "1.5rem",
            borderRadius: "8px",
            marginBottom: "1.5rem"
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "0.75rem 0",
              borderBottom: "2px solid #1c2a59",
              marginBottom: "1rem"
            }}>
              <span style={{ fontWeight: "600", color: "#1c2a59" }}>Annual Aggregate Limit</span>
              <span style={{ fontWeight: "bold", color: "#1c2a59" }}>LKR 20,000,000</span>
            </div>

            <div style={{
              display: "flex",
              alignItems: "flex-start",
              marginBottom: "0.75rem"
            }}>
              <span style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#1c2a59",
                marginTop: "6px",
                marginRight: "12px",
                flexShrink: 0
              }}></span>
              <div style={{ flex: 1, display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#545a75" }}>Any One Event</span>
                <span style={{ fontWeight: "600", color: "#1c2a59" }}>LKR 20,000,000</span>
              </div>
            </div>

            <div style={{
              display: "flex",
              alignItems: "flex-start"
            }}>
              <span style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#1c2a59",
                marginTop: "6px",
                marginRight: "12px",
                flexShrink: 0
              }}></span>
              <div style={{ flex: 1, display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#545a75" }}>Defence & Legal Costs</span>
                <span style={{ fontWeight: "600", color: "#1c2a59" }}>LKR 2,000,000</span>
              </div>
            </div>
          </div>

          {/* Policy Deductible */}
          <div style={{
            backgroundColor: "#f8f9fa",
            padding: "1rem 1.5rem",
            borderRadius: "8px",
            marginBottom: "1.5rem",
            display: "flex",
            justifyContent: "space-between"
          }}>
            <span style={{ color: "#545a75" }}>Policy Deductible</span>
            <span style={{ fontWeight: "600", color: "#1c2a59" }}>10% on each claim</span>
          </div>

          {/* Annual Premium */}
          <div style={{
            backgroundColor: "#007aff",
            color: "white",
            padding: "1.25rem 1.5rem",
            borderRadius: "8px",
            marginBottom: "1.5rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <span style={{ fontSize: "1rem" }}>Annual Premium (LKR)</span>
            <span style={{ fontSize: "1.75rem", fontWeight: "bold" }}>225,300</span>
          </div>

          {/* Thank You Message */}
          <div style={{
            backgroundColor: "#e3f2fd",
            padding: "1rem 1.5rem",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            gap: "12px"
          }}>
            <div style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "#0094f7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white"/>
              </svg>
            </div>
            <div style={{ fontSize: "0.9rem", color: "#1c2a59", lineHeight: "1.5" }}>
              One of our experts will contact you shortly.<br />
              <strong>Thank you!</strong>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ProfessionalIndemnity;
